import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { getServiceBySlug } from '@/config/services.config';

interface ServicePageProps {
    showBanner: () => void;
}

export const ServicePage: React.FC<ServicePageProps> = ({ showBanner }) => {
    const { slug } = useParams<{ slug: string }>();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(slug);
    const service = slug ? getServiceBySlug(slug) : undefined;

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!service) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto">
                        <h1 className="text-2xl font-bold text-white mb-4">Service provider not found</h1>
                        <Link to="/services" className="text-cyan-400 hover:text-cyan-300 underline">
                            View all services
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const [h1Main, h1Sub] = service.h1.includes(' — ')
        ? service.h1.split(' — ')
        : [service.h1, ''];

    const businessSchema = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: service.name,
        address: {
            '@type': 'PostalAddress',
            streetAddress: service.address.street,
            addressLocality: service.address.city,
            addressRegion: service.address.province,
            ...(service.address.postalCode ? { postalCode: service.address.postalCode } : {}),
            addressCountry: 'CA',
        },
        ...(service.phone ? { telephone: service.phone } : {}),
        ...(service.website ? { url: service.website } : {}),
        areaServed: service.serviceArea.join(', '),
        description: service.intro,
    };

    const faqSchema = service.faqs.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: service.faqs.map(f => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
    } : null;

    return (
        <>
            <SchemaMarkup schema={faqSchema ? [businessSchema, faqSchema] : businessSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            {/* H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-2">
                                {h1Main}
                                {h1Sub && (
                                    <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                        {h1Sub}
                                    </span>
                                )}
                            </h1>

                            {/* Intro */}
                            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6">{service.intro}</p>

                            {/* Quick suggestions */}
                            <QuickSuggestionsBlock
                                topLine="Not sure where to start?"
                                title={`Ask anything about ${service.serviceType.toLowerCase()} services.`}
                                suggestions={service.quickSuggestions}
                                onSend={sendMessage}
                            />

                            {/* About */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    About {service.name}
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300">{service.about}</p>
                            </div>

                            {/* Location and Contact */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Location and Contact Information
                                </h2>
                                <ul className="space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${service.name}, ${service.address.street}, ${service.address.city}, ${service.address.province}`)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                        >
                                            {service.address.street}, {service.address.city}, {service.address.province}{service.address.postalCode ? ` ${service.address.postalCode}` : ''}
                                        </a>
                                    </li>
                                    {service.phone && <li>Phone: {service.phone}</li>}
                                    {service.website && (
                                        <li>
                                            <a
                                                href={service.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-cyan-400 hover:text-cyan-300 underline transition-colors break-all"
                                            >
                                                {service.website}
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            {/* Service Categories */}
                            {service.serviceCategories.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        {service.serviceType} Services Offered
                                    </h2>
                                    {service.serviceCategories.map((cat, i) => (
                                        <div key={i} className="mb-3">
                                            <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                                {cat.category}
                                            </h3>
                                            <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                                {cat.items.map((item, j) => (
                                                    <li key={j}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Who It Helps */}
                            {service.whoItHelps.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Who {service.name} Helps
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                        {service.whoItHelps.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Service Area */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Service Area
                                </h2>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    {service.serviceArea.map((area, i) => (
                                        <li key={i}>{area}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Related Vehicles */}
                            {service.linkedVehicles.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Vehicles Commonly Associated With This Service
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {service.linkedVehicles.map((v, i) => (
                                            <li key={i}>
                                                <Link to={v.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {v.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Research Topics */}
                            {service.linkedResearch.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Research Vehicles Before Choosing a Service
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {service.linkedResearch.map((r, i) => (
                                            <li key={i}>
                                                <Link to={r.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {r.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Comparisons */}
                            {service.linkedComparisons.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Compare Vehicles
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                        {service.linkedComparisons.map((c, i) => (
                                            <li key={i}>
                                                <Link to={c.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {c.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* FAQ */}
                            {service.faqs.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Common Questions About {service.serviceType} in {service.city}
                                    </h2>
                                    <div className="space-y-4">
                                        {service.faqs.map((faq, i) => (
                                            <div key={i}>
                                                <h3 className="text-sm sm:text-base font-semibold text-gray-200 mb-1">{faq.question}</h3>
                                                <p className="text-sm sm:text-base text-gray-300">{faq.answer}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
